import { InputBaseComponentProps, Stack } from "@mui/material";
import {
  ClipboardEvent,
  FC,
  FocusEvent,
  forwardRef,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useFormContext } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import {
  useTextControlPatternValidation,
  useTextTransformationPattern,
} from "../../hooks";
import { errorLogger } from "../../utils";
import { HTextField, HTextFieldProps } from "../HTextField";
import { HErrorMessage } from "./HErrorMessage";
import { HFormField } from "./HFormField";

export type HTextFormFieldProps = HTextFieldProps & {
  name: string;
  format?: "text" | "currency";
  preventOnBlur?: boolean;
  preventOnChange?: boolean;
};

export type HNumberFormatFieldProps = InputBaseComponentProps;

export const HNumberFormatField = forwardRef(
  (props: HNumberFormatFieldProps, ref) => {
    const { onChange, ...other } = props;
    return (
      <NumericFormat
        {...other}
        defaultValue={other.defaultValue as string}
        suffix="€"
        thousandSeparator="."
        decimalSeparator=","
        onValueChange={values => {
          if (onChange) {
            onChange({
              target: {
                name: String(props.name),
                value: values.value,
              },
            } as any);
          }
        }}
      />
    );
  },
);

export const HTextFormField: FC<HTextFormFieldProps> = ({
  name,
  ref,
  format = "text",
  defaultValue,
  disableCopy = false,
  disablePaste = false,
  preventOnBlur,
  preventOnChange,
  normalizeText,
  normalizeTextForAddress,
  resetOnChange,
  required,
  textControlPattern,
  textTransformationPattern,
  autocomplete,
  autoComplete,
  ...props
}) => {
  const { getValues, setValue, trigger } = useFormContext();
  const { isTextPatternValid } = useTextControlPatternValidation({
    textControlPattern,
  });
  const { transformValue } = useTextTransformationPattern(
    textTransformationPattern,
  );
  const customInputComponent = useMemo(() => {
    switch (format) {
      case "currency": {
        return HNumberFormatField;
      }
      default: {
        return undefined;
      }
    }
  }, [format]);
  const inputRef = useRef<HTMLElement>(null);
  const validateValue = () => {
    const values = getValues();
    const fieldValue = values?.[name] ?? "";
    let parsedValue;

    if (normalizeText === true) {
      parsedValue = fieldValue
        .trim()
        .replace(/\s\s+/g, " ")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "'");
    } else if (normalizeTextForAddress === true) {
      parsedValue = fieldValue.trim().replace(/\s\s+/g, " ");
    }

    setValue(name, parsedValue);
  };

  const {
    onPaste: onPasteProps,
    onCopy: onCopyProps,
    onFocus: onFocusProps,
  } = props;

  const handleOnPaste = useCallback(
    (e: ClipboardEvent<HTMLDivElement>) => {
      if (disablePaste) {
        e.preventDefault();
        return false;
      }
      if (onPasteProps) {
        onPasteProps(e);
      }
    },
    [disablePaste, onPasteProps],
  );

  const handleOnCopy = useCallback(
    (e: ClipboardEvent<HTMLDivElement>) => {
      if (disableCopy) {
        e.preventDefault();
        return false;
      }
      if (onCopyProps) {
        onCopyProps(e);
      }
    },
    [disableCopy, onCopyProps],
  );

  const handleOnFocus = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      if (inputRef.current) {
        inputRef.current.scrollIntoView({
          behavior: "smooth",
          inline: "center",
          block: "center",
        });
      }
      if (onFocusProps) {
        onFocusProps(e);
      }
    },
    [onFocusProps],
  );

  return (
    <HFormField
      name={name}
      renderField={({ field, fieldState: { error } }) => {
        return (
          <Stack flex="1 1 100%" flexDirection="column">
            <HTextField
              autoComplete={autocomplete || autoComplete}
              spellCheck={false}
              helperText={props.helpText}
              {...field}
              {...props}
              value={field.value}
              onPaste={handleOnPaste}
              onCopy={handleOnCopy}
              disabled={props.disabled}
              inputRef={field.ref}
              InputProps={{
                ref: inputRef,
                ...props.InputProps,
                "aria-describedby": error?.message
                  ? `error-${name}`
                  : undefined,
                "aria-invalid": !!error || undefined,
                inputComponent: customInputComponent,
              }}
              onBlur={e => {
                if (
                  normalizeText === true ||
                  normalizeTextForAddress === true
                ) {
                  validateValue();
                }
                if (!preventOnBlur) {
                  field.onBlur();
                }
                if (props.onBlur) {
                  props.onBlur(e);
                }
                trigger(name).catch(errorLogger);
              }}
              onFocus={handleOnFocus}
              onChange={e => {
                let value = e.target.value;
                const isValueValid = isTextPatternValid(value);
                if (!isValueValid) {
                  return;
                }
                value = transformValue(value);
                const nextEvent = e;
                nextEvent.target.value = value;
                if (!preventOnChange) {
                  field.onChange(nextEvent);
                }
                if (props.onChange) {
                  props.onChange(nextEvent);
                }
              }}
              name={name}
            />
            {error?.message && (
              <HErrorMessage
                role="alert"
                id={`error-${name}`}
                errorMessage={error?.message}
              />
            )}
          </Stack>
        );
      }}
    />
  );
};
export default HTextFormField;
